import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Download, Trash2, ShieldCheck, MailCheck, History } from "lucide-react";

const CONSENT_PURPOSES = [
  { key: "terms", title: "Terms of Service", locked: true, note: "Required to use the service — delete your account to withdraw." },
  { key: "privacy", title: "Privacy Policy", locked: true, note: "Required to use the service." },
  { key: "data_processing", title: "Core service data processing", locked: true, note: "Required to deliver appointments, records and trust scores." },
  { key: "analytics", title: "Product analytics", locked: false, note: "We use anonymised usage to improve UX and reliability." },
  { key: "marketing", title: "Marketing communications", locked: false, note: "Occasional emails about product updates." },
  { key: "research", title: "Anonymised research inclusion", locked: false, note: "Contribute anonymised outcomes to Arogya Pramana research reports." },
];

const CHANGE_LABEL = {
  terms: "Terms", privacy: "Privacy Policy", data_processing: "Data processing",
  analytics: "Analytics", marketing: "Marketing", research: "Research",
};

export default function PrivacySettings() {
  const [consent, setConsent] = useState({});
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    const [c, h] = await Promise.all([
      api.get("/user/consent"),
      api.get("/user/consent-history"),
    ]);
    setConsent(c.data.consent || {});
    setHistory(h.data.entries || []);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (key) => {
    setBusy(true);
    try {
      const payload = { [`consent_${key}`]: !consent[key] };
      const { data } = await api.put("/user/consent", payload);
      setConsent(data.consent);
      // refresh history
      const { data: hd } = await api.get("/user/consent-history");
      setHistory(hd.entries || []);
      toast.success("Preference updated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/user/me/data-export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `arogya-pramana-export-${data.user.id}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data export has been downloaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const deleteAccount = async () => {
    setBusy(true);
    try {
      await api.delete("/user/me");
      setUser(false);
      toast.success("Your account has been deleted");
      navigate("/", { replace: true });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <AppShell pageTitle="Privacy & Data" pageDescription="Exercise your DPDP rights: access, correction, erasure, and consent management.">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Consent panel */}
        <div className="lg:col-span-2 rounded-lg border border-border bg-white p-6" data-testid="consent-panel">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-primary" />
            <h2 className="font-serif text-2xl text-primary">Consent management</h2>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Purpose-based consent, per DPDP Act §6. Required purposes cannot be withdrawn without deleting your account.
          </p>

          <ul className="mt-6 divide-y divide-border">
            {CONSENT_PURPOSES.map((p) => (
              <ConsentRow
                key={p.key}
                title={p.title}
                note={p.note}
                locked={p.locked}
                value={consent[p.key]}
                onToggle={p.locked ? undefined : () => toggle(p.key)}
                testid={`consent-toggle-${p.key}`}
              />
            ))}
          </ul>

          {consent.captured_at && (
            <p className="mt-6 text-xs text-muted-foreground">
              Consent captured on {new Date(consent.captured_at).toLocaleDateString("en-IN", { dateStyle: "medium" })} · policy version {consent.policy_version || "—"}
            </p>
          )}
        </div>

        {/* DPO card */}
        <div className="rounded-lg border border-border bg-primary text-primary-foreground p-6" data-testid="dpo-card">
          <div className="overline text-primary-foreground/70">Data Protection Officer</div>
          <h3 className="mt-2 font-serif text-2xl">Dr. Ananya Rao</h3>
          <p className="mt-1 text-sm text-primary-foreground/80">Grievance response SLA: 30 days.</p>
          <a href="mailto:dpo@arogyapramana.in" className="mt-4 inline-flex items-center gap-2 text-sm underline" data-testid="dpo-email-link">
            <MailCheck size={14} /> dpo@arogyapramana.in
          </a>
        </div>
      </div>

      {/* Consent audit trail */}
      <div className="mt-6 rounded-lg border border-border bg-white p-6" data-testid="consent-history-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History size={18} className="text-primary" />
            <h2 className="font-serif text-2xl text-primary">Consent audit trail</h2>
          </div>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{history.length} entries</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Every consent change is recorded with timestamp, policy version and source — a DPDP-mandatory audit log.
        </p>
        {history.length === 0 ? (
          <div className="mt-6 text-sm text-muted-foreground">No consent changes recorded yet.</div>
        ) : (
          <ul className="mt-4 divide-y divide-border max-h-96 overflow-y-auto" data-testid="consent-history-list">
            {history.map((h) => (
              <li key={h.id} className="py-3 text-sm" data-testid={`consent-history-entry-${h.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {Object.entries(h.changes || {}).map(([k, v]) => (
                      <span
                        key={k}
                        className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 ${v ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive"}`}
                      >
                        {CHANGE_LABEL[k] || k} {v ? "✓" : "✗"}
                      </span>
                    ))}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {new Date(h.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    {h.policy_version && <span> · v{h.policy_version}</span>}
                    {h.source && <span> · {h.source}</span>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Data export */}
        <div className="rounded-lg border border-pastel-mint-border bg-pastel-mint-bg p-6" data-testid="data-export-card">
          <Download size={18} className="text-pastel-mint-fg" />
          <h3 className="mt-3 font-serif text-2xl text-pastel-mint-fg">Export my data</h3>
          <p className="mt-1 text-sm text-pastel-mint-fg/85">
            Download a machine-readable JSON of your profile, records, appointments, and consent log (DPDP right to access).
          </p>
          <Button className="mt-4" onClick={download} disabled={busy} data-testid="data-export-button">
            <Download size={14} className="mr-1.5" /> Download JSON
          </Button>
        </div>

        {/* Delete account */}
        <div className="rounded-lg border border-destructive/30 bg-pastel-rose-bg p-6" data-testid="delete-account-card">
          <Trash2 size={18} className="text-destructive" />
          <h3 className="mt-3 font-serif text-2xl text-destructive">Delete my account</h3>
          <p className="mt-1 text-sm text-pastel-rose-fg/90">
            This permanently erases your profile, records, and consent history. Appointments you booked will be anonymised.
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="mt-4" data-testid="delete-account-button">
                <Trash2 size={14} className="mr-1.5" /> Delete account
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="font-serif text-2xl">Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This cannot be undone. Under DPDP right to erasure, we will delete your personal data immediately.
                  Aggregate/anonymised platform statistics may persist.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-account-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={deleteAccount} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-account-confirm">
                  Yes, delete my account
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </AppShell>
  );
}

function ConsentRow({ title, note, value, locked, onToggle, testid }) {
  return (
    <li className="py-3 flex items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="text-sm font-medium text-primary">{title}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{note}</div>
      </div>
      <Switch
        checked={!!value}
        disabled={locked}
        onCheckedChange={onToggle}
        data-testid={testid}
      />
    </li>
  );
}
